import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeft, Check, X } from 'lucide-react';

const API = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';
const LABELS = { casal: 'Casal', solteira: 'Solteira', solteiro: 'Solteiro' };

const AdminSocialApprovals = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/social-approvals`, { headers: headers() });
      setPending(r.data.pending || []);
    } catch {
      toast.error('Erro ao carregar candidatos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.role !== 'admin') { navigate('/dashboard'); return; }
    load();
  }, [user, navigate, load]);

  const act = async (uid, action) => {
    setBusy(uid);
    try {
      await axios.post(`${API}/admin/social-approvals/${uid}/${action}`, {}, { headers: headers() });
      toast.success(action === 'approve' ? 'Aprovado' : 'Rejeitado');
      setPending((prev) => prev.filter((p) => p.id !== uid));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="admin-social-approvals-page" className="min-h-screen bg-[#0D0D0D] text-white p-4 sm:p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/admin/dashboard')} className="p-2 rounded-lg hover:bg-white/5">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-[#C79F5E]">Triagem Rede Social</h1>
            <p className="text-gray-400 text-sm mt-1">Candidatos convidados por outro perfil Free — aprovar para liberar 100% do acesso.</p>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-gray-400 py-12">Carregando...</div>
        ) : pending.length === 0 ? (
          <div data-testid="social-approvals-empty" className="text-center text-gray-400 py-16 border border-[#2A2A2A] rounded-2xl bg-[#111]">
            Nenhum candidato em análise.
          </div>
        ) : (
          <div className="space-y-3">
            {pending.map((u) => (
              <div key={u.id} data-testid={`social-approval-row-${u.id}`} className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-white truncate">{u.name || '—'}</span>
                    <span className="text-xs text-gray-400 truncate">{u.email}</span>
                    <span className="bg-[#C79F5E]/20 text-[#C79F5E] text-xs font-bold px-2 py-0.5 rounded-full">
                      {LABELS[u.social_invite_type] || 'Free'}
                    </span>
                    <span className="bg-yellow-500/20 text-yellow-300 text-xs font-bold px-2 py-0.5 rounded-full">pending</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Convidado por:{' '}
                    <strong className="text-white">{u.inviter?.name || '—'}</strong>
                    <span className="text-gray-500 ml-1">({u.inviter?.email || '—'})</span>
                    <span className="ml-2">• Cadastro: {u.created_at ? new Date(u.created_at).toLocaleString('pt-BR') : '—'}</span>
                  </div>
                </div>
                <div className="flex gap-2 sm:justify-end">
                  <button
                    onClick={() => act(u.id, 'approve')}
                    disabled={busy === u.id}
                    className="px-4 py-2 rounded-lg font-bold text-sm bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] text-black disabled:opacity-50 hover:scale-[1.02] flex items-center gap-1"
                    data-testid={`social-approve-${u.id}`}
                  >
                    <Check className="w-4 h-4" /> Aprovar
                  </button>
                  <button
                    onClick={() => act(u.id, 'reject')}
                    disabled={busy === u.id}
                    className="px-4 py-2 rounded-lg font-semibold text-sm bg-[#2A0F0F] border border-red-500/50 text-red-300 hover:bg-[#3A1414] disabled:opacity-50 flex items-center gap-1"
                    data-testid={`social-reject-${u.id}`}
                  >
                    <X className="w-4 h-4" /> Rejeitar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSocialApprovals;
